import React, { useEffect, useRef, useState, useCallback } from "react";
import Cookies from "js-cookie";
import config from "../Config";
import { HandleResponse } from "../functions/HandleResponse";
import { authFetch } from "../functions/authFetch";
import { useNavigate } from "react-router-dom";

const MIN_DESC_WIDTH = 140;
// Fixed columns: Date(120) + Card(140) + Amount(90) + gaps(3*16) + padding(2*24) = ~470px
const FIXED_COLS_PX = 470;

const PaymentHistory = () => {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [descWidth, setDescWidth] = useState(MIN_DESC_WIDTH);
  const [clusterFilter, setClusterFilter] = useState("all");
  const tableRef = useRef(null);
  const dragState = useRef(null); // { startX, startWidth }
  const navigate = useNavigate();

  // Auto-size description column to fill available width on mount / resize
  useEffect(() => {
    const measure = () => {
      if (!tableRef.current) return;
      const available = tableRef.current.offsetWidth - FIXED_COLS_PX;
      setDescWidth(Math.max(MIN_DESC_WIDTH, available));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchPaymentHistory = async () => {
      try {
        const token = Cookies.get("runner_app");
        const response = await authFetch(`${config.AUTH_API}/v1/get-payment-history`, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        await HandleResponse(response, navigate);
        const data = await response.json();
        if (!cancelled) setPaymentHistory(data.payments || []);
      } catch (error) {
        if (!cancelled) console.error("Error fetching payment history:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPaymentHistory();
    return () => { cancelled = true; };
  }, [navigate]);

  // ── column resize ──────────────────────────────────────────────────────
  const onMouseMove = useCallback((e) => {
    if (!dragState.current) return;
    const delta = e.clientX - dragState.current.startX;
    setDescWidth(Math.max(MIN_DESC_WIDTH, dragState.current.startWidth + delta));
  }, []);

  const onMouseUp = useCallback(() => {
    dragState.current = null;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, [onMouseMove]);

  const startResize = useCallback((e) => {
    e.preventDefault();
    dragState.current = { startX: e.clientX, startWidth: descWidth };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [descWidth, onMouseMove, onMouseUp]);

  // ── helpers ────────────────────────────────────────────────────────────
  const formatDate = (raw) => {
    if (!raw) return "—";
    try {
      return new Date(raw).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch { return raw; }
  };

  const colStyle = {
    gridTemplateColumns: `120px ${descWidth}px 140px 90px`,
  };

  // Cluster names appear prefixed with the numeric account id (e.g. "8008-test");
  // strip it for a cleaner label while keeping the full name as the filter value.
  const clusterDisplay = (name) => (name || "").replace(/^\d+-/, "");

  // Distinct clusters present in the history, used to populate the filter.
  const clusterOptions = Array.from(
    new Set(paymentHistory.map((p) => p.cluster_name).filter(Boolean))
  ).sort();

  const visiblePayments =
    clusterFilter === "all"
      ? paymentHistory
      : paymentHistory.filter((p) => p.cluster_name === clusterFilter);

  return (
    <section className="acct-card">
      <div className="acct-card-header">
        <h2>Payment History</h2>
        {clusterOptions.length > 0 && (
          <select
            className="acct-history-filter"
            value={clusterFilter}
            onChange={(e) => setClusterFilter(e.target.value)}
            title="Filter by cluster"
          >
            <option value="all">All clusters</option>
            {clusterOptions.map((name) => (
              <option key={name} value={name}>
                {clusterDisplay(name)}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="acct-card-body">
        {loading ? (
          <p className="acct-empty">Loading…</p>
        ) : paymentHistory.length === 0 ? (
          <p className="acct-empty">No payment history found.</p>
        ) : visiblePayments.length === 0 ? (
          <p className="acct-empty">No payments for this cluster.</p>
        ) : (
          <div className="acct-history-table" ref={tableRef}>
            <div className="acct-history-head" style={colStyle}>
              <span>Date</span>
              <span className="acct-history-desc-head">
                Description
                <span
                  className="acct-history-resize-handle"
                  onMouseDown={startResize}
                  title="Drag to resize"
                />
              </span>
              <span>Payment Method</span>
              <span style={{ textAlign: 'right' }}>Amount</span>
            </div>
            {visiblePayments.map((payment, i) => (
              <div className="acct-history-row" key={i} style={colStyle}>
                <span className="acct-history-date">{formatDate(payment.payment_time)}</span>
                <span className="acct-history-desc" title={payment.description || undefined}>
                  {payment.description || "—"}
                </span>
                <span className="acct-history-card">
                  {payment.last4
                    ? `${payment.brand ? payment.brand.charAt(0).toUpperCase() + payment.brand.slice(1) + " " : ""}••••${payment.last4}`
                    : "—"}
                </span>
                <span className="acct-history-amount">${Number(payment.amount).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PaymentHistory;
